<script setup lang="ts">
import { computed, ref } from "vue";
import Button from "primevue/button";
import { Participant, ParticipantActivityType, Project } from "../../types/PersologOnlineTypes.ts";
import { ProfileAdministrationAPI, ProfileContractorAPI } from "../../api";
import JSZip from "jszip";
import { useI18n } from "vue-i18n";
import { getImpersonatedAccount, processInBatches } from "../../utils/GeneralUtils.ts";

const { t } = useI18n();

const props = defineProps({
    project: {
        type: Object as () => Project,
        required: true,
    },
    participants: {
        type: Array as () => Participant[],
        required: true,
    },
    withLabel: {
        type: Boolean,
        default: false,
    },
    withClasses: {
        type: String,
        default: "",
    },
});

const loading = ref(false);

const hasValidParticipants = computed(() =>
    props.participants.some((p) => p.status === "GENERATED" || p.status === "NOTIFIED"),
);

const downloadAll = async () => {
    loading.value = true;
    if (!hasValidParticipants.value) {
        return;
    }

    const zip = new JSZip();
    const participantsToDownload = props.participants.filter(
        (p) => p.status === "GENERATED" || p.status === "NOTIFIED",
    );

    const processParticipant = async (p: Participant) => {
        const { blob, filename } = await ProfileContractorAPI.authenticatedPdfDownload(
            props.project.trainer!,
            p.selectedProduct,
            p.id!,
            props.project.language,
        );
        await ProfileAdministrationAPI.logParticipantActivity(
            p.id!,
            ParticipantActivityType.PROFILE_DOWNLOADED_BY_TRAINER,
        );
        return { filename, blob };
    };

    const results = await processInBatches(participantsToDownload, processParticipant);

    results.forEach(({ filename, blob }) => {
        zip.file(filename, blob);
    });

    const content = await zip.generateAsync({ type: "blob" });
    const url = globalThis.URL.createObjectURL(content);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${props.project.name}.zip`;
    document.body.appendChild(a);
    a.click();
    globalThis.URL.revokeObjectURL(url);
    a.remove();
    loading.value = false;
};
</script>

<template>
    <div v-if="hasValidParticipants">
        <Button
            :class="withClasses"
            :disabled="getImpersonatedAccount() !== null"
            :outlined="!withLabel"
            :label="withLabel ? t('project-details.participants.callForAction.download') : ''"
            :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-download'"
            @click="downloadAll"
            v-tooltip.top="{ value: t('project-details.participants.tooltips.download'), showDelay: 500 }"
        />
    </div>
</template>
